import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { enUS } from '@mui/x-date-pickers/locales';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TimePicker from 'react-bootstrap-time-picker';
import { Col, Row } from 'react-bootstrap';
import { MdCircle } from 'react-icons/md';
import 'dayjs/locale/en';
import { toast } from 'react-toastify';

import {
  todoImportanceOptions,
  todoTypeOptions,
  todoRepeatOptions,
} from '../../../constant/commonData';
import { apiHeader, BaseURL } from '../../../config/apiUrl';
import { Post } from '../../../Axios/AxiosFunctions';
import dayjs from '../../../utils/timeUtils';
import { DropDown } from '../../../Component/DropDown/DropDown';
import { FaStop, FaMicrophone } from 'react-icons/fa';

export default function AddTodoDialog({
  open,
  setOpen,
  allAdmins,
  onSuccess = () => {},
  preDescription = '',
}) {
  const {
    access_token: accessToken,
    user,
    company_id: companyId,
  } = useSelector((state) => state?.authReducer);

  const [loading, setLoading] = useState(false);
  const [assignee, setAssignee] = useState([]);
  const [type, setType] = useState(todoTypeOptions[0]?.value);
  const [note, setNote] = useState('');
  const [importance, setImportance] = useState(todoImportanceOptions[0]?.value);
  const [deadline, setDeadline] = useState(null);
  const [milliseconds, setMilliseconds] = useState(null);
  const [isRepeat, setIsRepeat] = useState(false);
  const [repeatInterval, setRepeatInterval] = useState(
    todoRepeatOptions[0]?.value,
  );
  const [repeatEndTime, setRepeatEndTime] = useState(dayjs());
  const [isListening, setIsListening] = useState(false);
  const recognition = useRef(null);
  const [error, setError] = useState('');

  const startListening = () => {
    if (recognition.current && !isListening) {
      setError('');
      recognition.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
      setIsListening(false);
    }
  };

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Please use Chrome.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition.current = new SpeechRecognition();
    
    recognition.current.continuous = true;
    recognition.current.interimResults = true;
    recognition.current.lang = 'en-US';
    
    // Handle results
    recognition.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      
      if (finalTranscript) {
        setNote(prev => prev + finalTranscript);
      }
    };
    
    // Handle errors
    recognition.current.onerror = (event) => {
      setError(`Error: ${event.error}`);
      setIsListening(false);
    };
    
    // Handle end of recognition
    recognition.current.onend = () => {
      setIsListening(false);
    };
    
    return () => {
      if (recognition.current) {
        recognition.current.stop();
      }
    };
  }, []);

  const getTimeSeconds = (time) => {
    const m = time.get('minute');
    const h = time.get('hour');
    return Math.floor((h * 3600 + m * 60) / 1800) * 1800;
  };

  const handleMilliseconds = (ms) => {
    let ddlInDate = dayjs(deadline);
    if (!ddlInDate.isValid()) {
      console.log('Date format is invalid');
      return;
    }
    ddlInDate = ddlInDate
      .set('hour', Math.floor(ms / 3600))
      .set('minute', Math.floor((ms % 3600) / 60));
    setDeadline(ddlInDate);
    setMilliseconds(getTimeSeconds(ddlInDate));
  };

  const onSubmit = async () => {
    const url = BaseURL('todo');
    const body = {
      assignee: assignee?.map((item) => item?._id),
      note: `${note[0].toUpperCase()}${note.slice(1)}`,
      type,
      importance,
      dueDate: dayjs(deadline)?.toDate(),
      isRepeat,
      repeatInterval: repeatInterval,
      repeatEndTime: dayjs(repeatEndTime)?.toDate(),
    };

    setLoading(true);
    const res = await Post(url, body, apiHeader(accessToken), companyId);
    setLoading(false);
    if (res) {
      toast.success('Successfully created');
      onSuccess();
      onClose();

      setAssignee([]);
      setType(todoTypeOptions[0]?.value);
      setNote('');
      setImportance(todoImportanceOptions[0]?.value);
      setDeadline(null);
      setMilliseconds(null);
      setIsRepeat(false);
      setRepeatInterval(todoRepeatOptions[0]?.value);
      setRepeatEndTime(dayjs());

      return;
    }
    toast.error('Creation is failed');
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setDeadline(dayjs());
    setMilliseconds(3600 * 17);
    setAssignee([user]);
  }, []);

  useEffect(() => {
    setNote(preDescription);
  }, [preDescription]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="en"
      localeText={
        enUS.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Dialog open={open} onClose={onClose} fullWidth sx={{ zIndex: 1300 }}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
          }}
        >
          Add To-Do
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Row className="gy-3 mt-1">
            <Col lg={12}>
              <TextField
                multiline
                minRows={5}
                label="Task Note"
                name="note"
                value={note ? note[0].toUpperCase() + note.slice(1) : ''}
                onChange={(e) => setNote(e?.target?.value)}
                fullWidth
                autoFocus
                onFocus={function (e) {
                  var val = e.target.value;
                  e.target.value = '';
                  e.target.value = val;
                }}
              />
            </Col>
            <Col lg={12}>
              <IconButton
                IconButton
                onClick={startListening}
                sx={{ height: 30, width: 30, color: '#1976d2' }}
                disabled={isListening || !!error}
                size="small"
              >
                <FaMicrophone fontSize="medium" />
              </IconButton>
              <IconButton
                IconButton
                sx={{ height: 30, width: 30, color: '#d32f2f' }}
                size="small"
                disabled={!isListening}
                onClick={stopListening}
              >
                <FaStop fontSize="medium" />
              </IconButton>
            </Col>

            <Col md={12}>
              <DropDown
                setter={setAssignee}
                value={assignee}
                placeholder={'Select Assignee'}
                options={allAdmins}
                getOptionLabel={(option) => {
                  return `${option['firstName']} ${option['lastName']}`;
                }}
                optionValue={'_id'}
                isMulti={true}
              />
            </Col>
            <Col md={6}>
              <TextField
                name="type"
                label="Type *"
                value={type}
                onChange={(e) => setType(e?.target?.value)}
                select
                fullWidth
              >
                {todoTypeOptions.map((ele) => (
                  <MenuItem key={ele?.value} value={ele?.value}>
                    {ele?.label}
                  </MenuItem>
                ))}
              </TextField>
            </Col>
            <Col lg={6}>
              <TextField
                name="importance"
                label="Importance *"
                value={importance}
                onChange={(e) => setImportance(e?.target?.value)}
                select
                fullWidth
              >
                {todoImportanceOptions.map((ele) => (
                  <MenuItem key={ele?.value} value={ele?.value}>
                    <MdCircle
                      size={25}
                      style={{
                        color: ele?.value == 'lemon' ? 'gold' : ele?.value,
                        marginRight: '10px',
                      }}
                    />
                    {ele?.label}
                  </MenuItem>
                ))}
              </TextField>
            </Col>

            <Col md={6}>
              <DatePicker
                label={'Due Date *'}
                value={deadline}
                onChange={(e) => setDeadline(e)}
              />
            </Col>
            <Col md={6}>
              <TimePicker
                style={{
                  height: '3.5em',
                  padding: '1em',
                  font: 'inherit',
                  boxShadow: '0px 0 5px 2px #0000000d',
                  border: 'none',
                }}
                value={milliseconds}
                onChange={(e) => handleMilliseconds(e)}
              />
            </Col>

            <Col md={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRepeat}
                    onChange={(e) => setIsRepeat(e.target.checked)}
                  />
                }
                label="Repeat"
              />
            </Col>

            {isRepeat && (
              <>
                <Col md={6}>
                  <TextField
                    label=""
                    value={repeatInterval}
                    onChange={(e) => setRepeatInterval(e?.target?.value)}
                    select
                    fullWidth
                  >
                    {todoRepeatOptions.map((ele) => (
                      <MenuItem key={ele?.value} value={ele?.value}>
                        {ele?.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Col>
                <Col md={6}>
                  <DatePicker
                    label="Until"
                    value={repeatEndTime}
                    onChange={setRepeatEndTime}
                  />
                </Col>
              </>
            )}
          </Row>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={onSubmit} disabled={loading}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
